// ============================================================================
// SESSION HUB SERVICE
// Client links: the link token comes from the URL fragment and is only ever
// sent in JSON bodies. The visitor token is kept in sessionStorage, per link,
// and sent as a Bearer header. No cookies.
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const LINK_KEY = 'hub_link';
const visitorKey = (link: string) => `hub_visitor:${link.slice(0, 16)}`;

export interface HubSummary {
  title: string;
  kind: 'library' | 'pack';
  expiresAt: string;
}

export interface HubMe {
  hub: HubSummary;
  visitor: { email: string; displayName: string | null; isSponsor: boolean };
}

export interface HubWelcome {
  message: string | null;
  video: { url: string; expiresInSeconds: number } | null;
  captions: string | null;
}

export interface HubItem {
  itemId: string;
  sessionId: string;
  sessionName: string;
  state: 'available';
  title: string;
  description: string | null;
  kind: string;
  contentType: string;
  sizeBytes: number;
  publishedAt: string;
}

export type HubSessionStage = 'before' | 'live' | 'after';

export interface HubSession {
  sessionId: string;
  name: string;
  stage: HubSessionStage;
  attending: boolean;
  mode: 'in_person' | 'remote_teams' | null;
  sharedItems: number;
}

export interface HubAgendaItem { time: string; title: string; note?: string }

export interface HubBefore {
  session: {
    sessionId: string;
    name: string;
    stage: HubSessionStage;
    days: Array<{ date: string; startTime: string | null; endTime: string | null }>;
    roomName: string | null;
    leadFacilitator: string | null;
    mode: 'in_person' | 'remote_teams' | null;
  };
  gettingThere: { address: string | null; arrivalInstructions: string | null; accessNotes: string | null; teamsJoinNote: string | null };
  agenda: Array<{ date: string; items: HubAgendaItem[] }>;
  objective: { text: string; confirmed: boolean; canRespond: boolean; status: 'awaiting_sponsor' | 'confirmed' | 'change_suggested' | null; suggestion: string | null } | null;
  hope: { text: string | null; canEdit: boolean } | null;
}

export interface HubItemLink {
  url: string;
  contentType: string;
  expiresInSeconds: number;
}

export class HubError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}, visitorToken?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (visitorToken) headers.Authorization = `Bearer ${visitorToken}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'omit', referrerPolicy: 'no-referrer' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new HubError(body.error || `Request failed (${res.status})`, res.status);
  return body as T;
}

const store = {
  get: (key: string) => { try { return sessionStorage.getItem(key); } catch { return null; } },
  set: (key: string, value: string) => { try { sessionStorage.setItem(key, value); } catch { /* private mode */ } },
  remove: (key: string) => { try { sessionStorage.removeItem(key); } catch { /* private mode */ } },
};

export const sessionHubService = {
  /**
   * Takes the link token from #t=… once, keeps it for this tab, and removes it
   * from the address bar so it is not bookmarked, shared or sent as a referrer.
   */
  takeLinkFromUrl(): string | null {
    const match = /(?:^|[#&])t=([A-Za-z0-9_-]{32,128})/.exec(window.location.hash);
    if (match) {
      store.set(LINK_KEY, match[1]);
      window.history.replaceState(null, '', window.location.pathname);
      return match[1];
    }
    return store.get(LINK_KEY);
  },

  resolve: (link: string) => call<HubSummary>('/api/hub/resolve', { method: 'POST', body: JSON.stringify({ hubToken: link }) }),

  requestCode: (link: string, email: string) =>
    call<{ message: string }>('/api/hub/code', { method: 'POST', body: JSON.stringify({ hubToken: link, email }) }),

  async verify(link: string, email: string, code: string): Promise<void> {
    const r = await call<{ token: string }>('/api/hub/verify', { method: 'POST', body: JSON.stringify({ hubToken: link, email, code }) });
    store.set(visitorKey(link), r.token);
  },

  visitorToken: (link: string) => store.get(visitorKey(link)),

  me: (link: string) => call<HubMe>('/api/hub/me', {}, store.get(visitorKey(link)) ?? undefined),

  welcome: (link: string) => call<HubWelcome>('/api/hub/welcome', {}, store.get(visitorKey(link)) ?? undefined),

  sessions: (link: string) => call<{ sessions: HubSession[] }>('/api/hub/sessions', {}, store.get(visitorKey(link)) ?? undefined),

  sessionWelcome: (link: string, sessionId: string) =>
    call<HubWelcome>(`/api/hub/sessions/${encodeURIComponent(sessionId)}/welcome`, {}, store.get(visitorKey(link)) ?? undefined),

  before: (link: string, sessionId: string) =>
    call<HubBefore>(`/api/hub/sessions/${encodeURIComponent(sessionId)}/before`, {}, store.get(visitorKey(link)) ?? undefined),

  respondToObjective: (link: string, sessionId: string, response: 'confirm' | 'suggest', suggestion?: string) =>
    call<void>(`/api/hub/sessions/${encodeURIComponent(sessionId)}/objective`, { method: 'PUT', body: JSON.stringify({ response, suggestion }) }, store.get(visitorKey(link)) ?? undefined),

  saveHope: (link: string, sessionId: string, text: string | null) =>
    call<{ text: string | null }>(`/api/hub/sessions/${encodeURIComponent(sessionId)}/hope`, { method: 'PUT', body: JSON.stringify({ text }) }, store.get(visitorKey(link)) ?? undefined),

  items: (link: string) => call<{ items: HubItem[] }>('/api/hub/items', {}, store.get(visitorKey(link)) ?? undefined),

  itemLink: (link: string, itemId: string, mode: 'view' | 'download') =>
    call<HubItemLink>(`/api/hub/items/${encodeURIComponent(itemId)}/${mode}`, {}, store.get(visitorKey(link)) ?? undefined),

  signOut(link: string) {
    store.remove(visitorKey(link));
  },
};
